import { isAxiosError } from 'axios';

/**
 * The zafroi-mvp-runtime envelope:
 *   { success: false, error: { code, message }, meta: { requestId, timestamp } }
 */
export interface ApiErrorPayload {
  code: string;
  message: string;
  details?: unknown;
}

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly requestId?: string;
  readonly details?: unknown;

  constructor(opts: {
    message: string;
    code: string;
    status: number;
    requestId?: string;
    details?: unknown;
  }) {
    super(opts.message);
    this.name = 'ApiError';
    this.code = opts.code;
    this.status = opts.status;
    this.requestId = opts.requestId;
    this.details = opts.details;
  }

  get isNotFound() {
    return this.status === 404 || this.code === 'Not Found';
  }
  get isUnauthorized() {
    return this.status === 401 || this.code === 'Unauthorized';
  }
  get isForbidden() {
    return this.status === 403;
  }
  get isNetwork() {
    return this.status === 0;
  }
  get isValidation() {
    return this.status === 422 || this.code === 'Validation';
  }
  get isSubscriptionExpired() {
    return this.code === 'SubscriptionExpired';
  }
  get isSupportSessionRequired() {
    return this.code === 'SupportSessionRequired';
  }
  get isSessionAlreadyActive() {
    return this.code === 'SessionAlreadyActive';
  }
}

export function toApiError(err: unknown): ApiError {
  if (err instanceof ApiError) return err;

  if (isAxiosError(err)) {
    const status = err.response?.status ?? 0;
    const data = err.response?.data as
      | { error?: ApiErrorPayload; meta?: { requestId?: string } }
      | undefined;
    return new ApiError({
      message: data?.error?.message ?? err.message ?? 'Request failed',
      code: data?.error?.code ?? (status === 0 ? 'Network Error' : `HTTP ${status}`),
      status,
      requestId: data?.meta?.requestId,
      details: data?.error?.details,
    });
  }

  if (err instanceof Error) {
    return new ApiError({ message: err.message, code: 'Unknown', status: 0 });
  }

  return new ApiError({ message: 'Unknown error', code: 'Unknown', status: 0 });
}
